import { ErrorMessage, Field } from '@atlaskit/form';
import Select, { OptionProps, ValueType } from '@atlaskit/select';
import React from 'react';
import Textfield from '@atlaskit/textfield';
import { BenefitCustomisableValue, Promotion } from '@css-xp/graphtypes';
import { FormattedDate } from '../../formatted';
import { PromotionDuration, ReasonCustomisableObj } from '@atlassian/cssxp-types-promotion';
import toNumber from 'lodash/toNumber';
import capitalize from 'lodash/capitalize';
import startCase from 'lodash/startCase';
import get from 'lodash/get';
import './select-promotion-form-field.scss';

export interface SelectPromotionSelectItem {
  value: string; // Promotion Id
  promotion: Promotion;
}

const getActivePromotions = (promotions: any) => {
  return (promotions ?? [])?.filter((promotion: Promotion) => {
    return promotion.status === 'ACTIVE';
  });
};

const SelectPromotionSelectOption = ({
  data,
  getStyles,
  innerProps,
  innerRef,
  ...props
}: OptionProps<SelectPromotionSelectItem>) => {
  const { promotion } = data as { promotion: Promotion };
  const optionStyles = getStyles('option', props);
  const promotionStartTime = promotion && promotion.promotionWindow && promotion.promotionWindow.startTime;
  const promotionEndTime = promotion && promotion.promotionWindow && promotion.promotionWindow.endTime;
  const promotionBenefits = (promotion && promotion.benefits) || [];
  const promotionTitle = promotion && promotion.title;

  return (
    <section ref={innerRef} {...innerProps} style={optionStyles} key={`promotion-select-option-${promotion?.id}`}>
      <span data-testid={`promotion-select-option-${promotion?.id}`}>
        {!promotion?.id && (
          <>
            <strong>No promotions</strong>
            <br />
          </>
        )}
        {promotionTitle && (
          <div>
            <strong>{promotionTitle}</strong>
          </div>
        )}
        {promotionBenefits &&
          promotionBenefits.length !== 0 &&
          promotionBenefits.map(
            (benefit) =>
              benefit && (
                <span key={`${benefit.benefitType}-${benefit.value}`}>
                  <span>
                    Benefit type: {benefit.benefitType}, Subtype: {benefit.subBenefitType}, Value: {benefit.value}
                  </span>
                  <br />
                </span>
              )
          )}
        Promotion window:{' '}
        {promotionStartTime && promotionEndTime ? (
          <span>
            {promotionStartTime && <FormattedDate date={promotionStartTime} />}
            {promotionEndTime && (
              <>
                {' - '}
                <FormattedDate date={promotionEndTime} />
              </>
            )}
          </span>
        ) : (
          <span className="data-unavailable">N/A</span>
        )}
      </span>
    </section>
  );
};

interface Props {
  fieldName?: string;
  fieldLabel?: React.ReactNode;
  isRequired?: boolean;
  availablePromotions?: Promotion[] | null;
  promotions?: Promotion[] | null;
  isDisabled?: boolean | false;
  includeAllPromotions?: boolean | false;
  defaultValueRewindPane?: boolean | false;
}

export interface SelectCustomPromotionItem {
  value: string;
  label: string;
}

// TODO: perhaps will remove these default values once customisable is available in promotion object.
const DefaultValueOption = {
  type: 'range',
  lowerBound: 10.0,
  upperBound: 100.0,
};

enum DefaultBoundvalue {
  LOWER_BOUND = 0,
  UPPER_BOUND = 100,
}

const DefaultDurationOption = ['ONCE', 'FOREVER', 'RECURRING'];

const DefaultIterationOption = {
  type: 'range',
  lowerBound: 1,
  upperBound: 3,
};

const hasNoIterationsCheck = (value: string) => value === PromotionDuration.ONCE || value === PromotionDuration.FOREVER;

const CustomPromotionField = ({ promotion }: SelectPromotionSelectItem) => {
  const firstBenefitItem = promotion?.benefits?.[0];
  const initialIterationsFieldState = hasNoIterationsCheck(firstBenefitItem?.duration ?? '');

  const [isIterationsFieldVisible, setIsIterationsFieldVisible] = React.useState<boolean>(initialIterationsFieldState);

  React.useEffect(() => {
    // Make sure that correct value is set for initial and subsequent renderings
    setIsIterationsFieldVisible(initialIterationsFieldState);
  }, [initialIterationsFieldState]);

  const customPromotionOptions = get(promotion, 'applicationReason.customisable.id.anyOf', [])?.map(
    (reasonCodes: ReasonCustomisableObj) => {
      return {
        value: reasonCodes?.id,
        label: startCase(reasonCodes?.readableName),
      };
    }
  );

  const isCustomReasonCodeEnabled = promotion?.applicationReason?.customisable?.id;
  const isCustomValueEnabled = firstBenefitItem?.customisable?.value;
  const isCustomDurationEnabled = firstBenefitItem?.customisable?.duration;
  const isCustomIterationEnabled = firstBenefitItem?.customisable?.iterations;

  const customDurationOptions = get(firstBenefitItem, 'customisable.duration', DefaultDurationOption)?.map(
    (duration: string) => ({
      value: duration,
      label: capitalize(duration ?? ''),
    })
  );

  const customValueOptions = firstBenefitItem?.customisable?.value ?? DefaultValueOption;
  const customIterationOptions = firstBenefitItem?.customisable?.iterations ?? DefaultIterationOption;

  const validator = (value: any, validatorRef: BenefitCustomisableValue) => {
    if (!Number.isInteger(toNumber(value))) {
      return 'Must be a number without decimal point';
    }
    if (
      toNumber(value) < toNumber(validatorRef?.lowerBound ?? DefaultBoundvalue.LOWER_BOUND) ||
      toNumber(value) > toNumber(validatorRef?.upperBound ?? DefaultBoundvalue.UPPER_BOUND)
    ) {
      return `Value cannot be less than ${validatorRef?.lowerBound ?? DefaultBoundvalue.LOWER_BOUND} and greater than ${
        validatorRef?.upperBound ?? DefaultBoundvalue.UPPER_BOUND
      }`;
    }
    return undefined;
  };

  const defaultSelectedReason: SelectCustomPromotionItem = {
    value: promotion?.applicationReason?.id ?? '',
    label: startCase(promotion?.applicationReason?.readableName ?? ''),
  };

  const defaultSelectedDuration: SelectCustomPromotionItem = {
    value: firstBenefitItem?.duration ?? '',
    label: capitalize(firstBenefitItem?.duration ?? ''),
  };

  return promotion?.isPromotionCustomisable ? (
    <div className="border-form" data-testid={'custom-promotion-form'}>
      <Field<ValueType<boolean>> name={'customisable'} label={''} defaultValue={true}>
        {() => (
          <>
            {isCustomReasonCodeEnabled && (
              <Field<ValueType<SelectCustomPromotionItem>, HTMLSelectElement>
                name={'customReason'}
                label={'Reason'}
                isRequired={true}
                defaultValue={defaultSelectedReason}
              >
                {({ fieldProps }) => (
                  <Select<SelectCustomPromotionItem>
                    {...fieldProps}
                    options={customPromotionOptions}
                    isSearchable={false}
                    data-testid="custom-promotion-reason-drop-down"
                  />
                )}
              </Field>
            )}
            {isCustomValueEnabled && (
              <Field
                name="customValue"
                label="Discount Value (%)"
                isRequired={true}
                validate={(value: any) => validator(value, customValueOptions)}
                defaultValue={firstBenefitItem?.value ?? '0'}
              >
                {({ fieldProps, error }) => (
                  <>
                    <Textfield {...fieldProps} name="customValue" />
                    {error && <ErrorMessage>{error}</ErrorMessage>}
                  </>
                )}
              </Field>
            )}

            {isCustomDurationEnabled && (
              <Field<ValueType<SelectCustomPromotionItem>, HTMLSelectElement>
                name={'customDuration'}
                label={'Duration'}
                isRequired={true}
                defaultValue={defaultSelectedDuration}
              >
                {({ fieldProps }) => (
                  <Select<SelectCustomPromotionItem>
                    {...fieldProps}
                    options={customDurationOptions}
                    isSearchable={false}
                    data-testid="custom-promotion-duration-drop-down"
                    // @ts-ignore
                    onChange={(selectedItem: SelectCustomPromotionItem) => {
                      const hasNoIterations = hasNoIterationsCheck(selectedItem.value);
                      setIsIterationsFieldVisible(hasNoIterations);
                      fieldProps.onChange(selectedItem);
                    }}
                  />
                )}
              </Field>
            )}
            {isCustomIterationEnabled &&
              (isIterationsFieldVisible || (
                <Field
                  name={'customIterations'}
                  label={'Iterations'}
                  isRequired={true}
                  validate={(value: any) => validator(value, customIterationOptions)}
                  defaultValue={firstBenefitItem?.iterations ?? '1'}
                >
                  {({ fieldProps, error }) => (
                    <>
                      <Textfield
                        {...fieldProps}
                        name="customIterations"
                        data-testid="custom-promotion-iteration-text-field"
                      />
                      {error && <ErrorMessage>{error}</ErrorMessage>}
                    </>
                  )}
                </Field>
              ))}
          </>
        )}
      </Field>
    </div>
  ) : null;
};

export const SelectPromotionFormField = ({
  fieldName = 'promotionId',
  fieldLabel = 'Select promotion',
  isRequired = false,
  availablePromotions = [],
  promotions = [],
  isDisabled,
  includeAllPromotions = false,
  defaultValueRewindPane = false,
}: Props) => {
  if ((!availablePromotions || availablePromotions.length === 0) && (!promotions || promotions.length === 0)) {
    return null;
  }

  const emptyPromotionItem = {
    value: '',
    promotion: {
      id: '',
    },
    label: 'No Promotions, Promotion window: N/A',
  } as SelectPromotionSelectItem;
  const activeAvailablePromotions = getActivePromotions(availablePromotions);
  const selectItems = activeAvailablePromotions.map((promotion: Promotion) => {
    return {
      promotion,
      value: promotion.id,
    } as SelectPromotionSelectItem;
  });
  selectItems.unshift(emptyPromotionItem);

  const activePromotions = includeAllPromotions ? availablePromotions : activeAvailablePromotions;
  const hasPromotion = activePromotions.length !== 0;
  // Note: The first active promotion is applied as a default value
  const defaultValue = hasPromotion
    ? promotions && promotions.length > 0
      ? {
          promotion: promotions[0],
          value: promotions[0].id || '',
          customReason: {
            value: promotions[0].applicationReason?.id || '',
            label: promotions[0].applicationReason?.readableName || '',
          },
          customValue: promotions[0].benefits?.[0]?.value || 0,
          customIterations: promotions[0].benefits?.[0]?.iterations || 0,
          customDuration: {
            value: promotions[0].benefits?.[0]?.duration || '',
            label: promotions[0].benefits?.[0]?.duration || '',
          },
          customisable: promotions[0].isPromotionCustomisable ?? false,
          isIterationFieldEnabled: promotions[0].benefits?.[0]?.duration === PromotionDuration.REPEATING ? true : false,
          label: promotions[0].title,
        }
      : defaultValueRewindPane
      ? emptyPromotionItem
      : null
    : defaultValueRewindPane
    ? emptyPromotionItem
    : undefined;

  return (
    <div style={{ maxWidth: '500px' }}>
      <Field<ValueType<SelectPromotionSelectItem>, HTMLSelectElement>
        name={fieldName}
        label={fieldLabel}
        isRequired={isRequired}
        defaultValue={defaultValue}
        isDisabled={isDisabled}
      >
        {({ fieldProps }) => (
          <div data-testid="select-promotion-dropdown">
            <Select<SelectPromotionSelectItem>
              {...fieldProps}
              options={selectItems}
              className="select-promotion-dropdown"
              classNamePrefix="select-promotion-dropdown"
              components={{
                Option: SelectPromotionSelectOption,
                //@ts-ignore
                SingleValue: SelectPromotionSelectOption,
              }}
              isSearchable={false}
            />
            {<CustomPromotionField {...(fieldProps.value as SelectPromotionSelectItem)} />}
          </div>
        )}
      </Field>
    </div>
  );
};
